import { Box, FormControlLabel, Switch } from "@mui/material";
import { createUseStyles } from "$utils/createUseStyles";
import { useEffect, useState } from "react";
import { theme } from "$utils/theme/bataillog";
import ActivitiesSelect from "$forms/ActivitiesSelect";
import AddressSelect from "$forms/AddressSelect";
import CompanySelect from "$forms/CompanySelect";
import ContactSelect from "$forms/ContactSelect";
import { getActivitiesOptions } from "$utils/typeConstants";

import { useActivitiesQuery } from "$hooks/useActivitiesQuery";
import CategorySelect from "$forms/CategorySelect";
import { Text } from "$components";
import CompanyTypesCheckboxes from "$forms/CompanyTypesCheckboxes/CompanyTypesCheckboxes";
import { CompanyType } from "$types";
import DockyardSelect from "$forms/DockyardSelect";
import PrimarySearchAppBar from "$components/AppBar/SearchBar";
import ProductSelect from "$forms/ProductSelect";
import sections from "./sections";
import Block from "./Block";
import Divider from "./Divider";

const selectorsSection = sections.find((s) => s.id === "selectors");
const firstCompanyIsUsedLabel =
  "(la première entreprise sélectionnée est utilisée)";

function Selectors() {
  const classes = useStyles();
  const { data: allActivities = [] } = useActivitiesQuery();

  const [areMultiple, setAreMultiple] = useState(false);
  const [activities, setActivities] = useState<string | Array<string>>();
  const [categories, setCategories] = useState<string | Array<string>>();
  const [companies, setCompanies] = useState<string | Array<string>>();
  const [companyTypes, setCompanyTypes] = useState<Array<CompanyType>>([
    CompanyType.CLIENT,
  ]);
  const [contacts, setContacts] = useState<string | Array<string>>();
  const [dockyards, setDockyards] = useState<string | Array<string>>();
  const [products, setProducts] = useState<string | Array<string>>();

  const selectedCompanyId = Array.isArray(companies) ? companies[0] : companies;

  useEffect(() => {
    if (areMultiple) {
      setActivities((prevValue) => [prevValue as string]);
      setCategories((prevValue) => [prevValue as string]);
      setCompanies((prevValue) => [prevValue as string]);
      setContacts((prevValue) => [prevValue as string]);
      setDockyards((prevValue) => [prevValue as string]);
      setProducts((prevValue) => [prevValue as string]);
    } else {
      setActivities((prevValue) => prevValue?.[0]);
      setCategories((prevValue) => prevValue?.[0]);
      setCompanies((prevValue) => prevValue?.[0]);
      setContacts((prevValue) => prevValue?.[0]);
      setDockyards((prevValue) => prevValue?.[0]);
      setProducts((prevValue) => prevValue?.[0]);
    }
  }, [areMultiple]);

  return (
    <Block id={selectorsSection.id} title={selectorsSection.label}>
      <Box
        display="flex"
        flexDirection="column"
        style={{ gap: theme.spacing(2) }}
      >
        <Text>
          <i>{`Certains sélecteurs ont besoin qu'une entreprise soit
          sélectionnée au préalable. Dans ce cas, la première entreprise
          sélectionnée dans le sélecteur d'entreprise est utilisée.`}</i>
        </Text>
        <Text>
          <i>
            Autant que possible, les sélecteurs sont utilisés avec leurs
            propriétés <b>par défaut</b>.
          </i>
        </Text>
      </Box>

      <Divider />

      <Box marginY={2}>
        <Text type="h3" style={{ margin: 0 }}>
          Contrôles
        </Text>
        <FormControlLabel
          control={
            <Switch
              checked={areMultiple}
              onChange={() => setAreMultiple(!areMultiple)}
              color="primary"
            />
          }
          label="Sélecteurs multiples ?"
        />
      </Box>

      <Divider />

      <Box
        className={classes.columnsWithGap}
        display="flex"
        flexDirection="column"
      >
        <Box display="flex" style={{ gap: theme.spacing(2) }}>
          <Box flex={1}>
            <CompanySelect
              companyTypes={companyTypes}
              multiple={areMultiple}
              onChange={setCompanies}
              value={companies}
              minCharsToTrigerSearch={0}
              rowsPerPage={99999}
              label="Entreprise(s)"
            />
          </Box>
          <Box flex={1}>
            <CompanyTypesCheckboxes
              onChange={setCompanyTypes}
              value={companyTypes}
            />
          </Box>
        </Box>
        <ActivitiesSelect
          activitiesOptions={getActivitiesOptions(allActivities)}
          multiple={areMultiple}
          onChange={setActivities}
          value={activities}
        />
        <AddressSelect value={null} onChange={() => null} />
        <CategorySelect
          companyId={selectedCompanyId}
          label={`Catégorie(s) ${firstCompanyIsUsedLabel}`}
          multiple={areMultiple}
          onChange={setCategories}
          showReferencingCategory
          value={categories}
        />

        <ContactSelect
          companyId={selectedCompanyId}
          label={`Contact(s) ${firstCompanyIsUsedLabel}`}
          multiple={areMultiple}
          onChange={setContacts}
          value={contacts}
        />

        <DockyardSelect
          companyId={selectedCompanyId}
          label={`Chantier(s) ${firstCompanyIsUsedLabel}`}
          multiple={areMultiple}
          onChange={setDockyards}
          value={dockyards}
        />

        <ProductSelect
          companyId={selectedCompanyId}
          label={`Produit(s) ${firstCompanyIsUsedLabel}`}
          multiple={areMultiple}
          onChange={setProducts}
          value={products}
        />
      </Box>

      <Divider />

      <Text type="h3">Master filtre</Text>
      <PrimarySearchAppBar />
    </Block>
  );
}

export default Selectors;

const useStyles = createUseStyles(() => ({
  columnsWithGap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));
